import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
    Box, Flex, Text, Link
} from 'components/ui';
import { wrapFunctionalComponent } from 'utils/framework';
import Markdown from 'components/Markdown/Markdown';
import InfoButton from 'components/InfoButton/InfoButton';
import SDUBanner from 'components/ProductPage/DeliveryOptions/SDUBanner';
import SDUBannerNDD from 'components/ProductPage/DeliveryOptions/SDUBannerNDD';
import helpersUtils from 'utils/Helpers';
import languageLocale from 'utils/LanguageLocale';
import { renderModal, globalModals } from 'utils/globalModals';
import { isSDUAddedToBasketSelector } from 'viewModel/selectors/basket/isSDUAddedToBasketSelector';
import { globalModalsSelector } from 'selectors/page/headerFooterTemplate/data/globalModals/globalModalsSelector';
import FulfillmentMethodsEN from 'components/ProductPage/FulfillmentMethods/locales/FulfillmentMethods_en_US';
import FulfillmentMethodsFR from 'components/ProductPage/FulfillmentMethods/locales/FulfillmentMethods_fr_CA';

const { replaceDoubleAsterisks } = helpersUtils;
const { SDD_FULFILLMENT_SERVICE_INFO } = globalModals;
const getText = languageLocale.getLocalisedDataFromResource(FulfillmentMethodsEN, FulfillmentMethodsFR);

const styles = {
    cutoffMarkdown: {
        '& > :first-child': {
            display: 'inline'
        }
    }
};

const FastDeliveryDetailsPanel = ({
    availabilityText,
    locationText,
    forText,
    sameDayAvailabilityStatus,
    availabilityTextColor,
    cutoffMessage,
    sddRougeTestV2FreeShippingMessage,
    nddEnabled,
    skuTrialEligibility,
    skuTrialPeriod,
    onOpenSDULandingPage,
    onShowSDDRougeTestV2InfoModal,
    onLocationClick
}) => {
    const isSDUAddedToBasket = useSelector(isSDUAddedToBasketSelector);
    const globalModalsData = useSelector(globalModalsSelector);
    const sddFulfillmentServiceInfoModal = globalModalsData[SDD_FULFILLMENT_SERVICE_INFO];

    const handleInfoClick = useCallback(
        e => {
            e?.preventDefault();
            e?.stopPropagation();
            renderModal(sddFulfillmentServiceInfoModal);
        },
        [sddFulfillmentServiceInfoModal]
    );

    const showRougeV2InfoBox = Boolean(sddRougeTestV2FreeShippingMessage) && !isSDUAddedToBasket;

    return (
        <Box>
            <Flex
                alignItems='center'
                justifyContent='space-between'
                width='100%'
                marginBottom={cutoffMessage ? 2 : 0}
            >
                <Text
                    is='p'
                    fontSize='sm'
                >
                    <Text
                        is='span'
                        data-at={Sephora.debug.dataAt('pdp_see_availability_label')}
                        fontWeight={sameDayAvailabilityStatus && 'bold'}
                        color={availabilityTextColor}
                        children={availabilityText}
                    />
                    {` ${forText} `}
                    <Link
                        onClick={onLocationClick}
                        arrowDirection='down'
                        fontSize='sm'
                        fontWeight='bold'
                        padding={2}
                        margin={-2}
                        aria-label={getText('fastDeliveryPanelZipAriaLabel')}
                        children={locationText}
                    />
                </Text>
                <InfoButton
                    size={16}
                    onClick={handleInfoClick}
                    aria-label={getText('fastDeliveryPanelInfoAriaLabel')}
                />
            </Flex>
            {cutoffMessage && (
                <Text
                    is='p'
                    fontSize='sm'
                    color={availabilityTextColor}
                >
                    <Markdown
                        is='span'
                        css={styles.cutoffMarkdown}
                        content={replaceDoubleAsterisks(cutoffMessage)}
                    />
                </Text>
            )}
            {showRougeV2InfoBox ? (
                <Box
                    is='p'
                    backgroundColor='white'
                    paddingX={3}
                    paddingY={2}
                    borderWidth={1}
                    borderColor='lightGray'
                    borderRadius={2}
                    marginTop={2}
                >
                    <Text
                        marginRight={1}
                        children={sddRougeTestV2FreeShippingMessage}
                    />
                    <InfoButton
                        size={16}
                        onClick={onShowSDDRougeTestV2InfoModal}
                    />
                </Box>
            ) : !nddEnabled ? (
                <Box marginTop={2}>
                    <SDUBanner
                        isSDUAddedToBasket={isSDUAddedToBasket}
                        skuTrialEligibility={skuTrialEligibility}
                        skuTrialPeriod={skuTrialPeriod}
                        renderSDULandingPage={onOpenSDULandingPage}
                    />
                </Box>
            ) : null}
            {nddEnabled && !showRougeV2InfoBox && (
                <SDUBannerNDD
                    variant='fulfillmentChallenger'
                    skuTrialEligibility={skuTrialEligibility}
                    renderSDULandingPage={onOpenSDULandingPage}
                />
            )}
        </Box>
    );
};

FastDeliveryDetailsPanel.propTypes = {
    availabilityText: PropTypes.string,
    locationText: PropTypes.string,
    forText: PropTypes.string,
    sameDayAvailabilityStatus: PropTypes.string,
    availabilityTextColor: PropTypes.string,
    cutoffMessage: PropTypes.string,
    sddRougeTestV2FreeShippingMessage: PropTypes.string,
    nddEnabled: PropTypes.bool,
    skuTrialEligibility: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    skuTrialPeriod: PropTypes.string,
    onOpenSDULandingPage: PropTypes.func.isRequired,
    onShowSDDRougeTestV2InfoModal: PropTypes.func.isRequired,
    onLocationClick: PropTypes.func.isRequired
};

export default wrapFunctionalComponent(FastDeliveryDetailsPanel, 'FastDeliveryDetailsPanel');
